import { atom, getDefaultStore } from 'jotai';

export type DownBackend = 'sanr' | 'arena';

// Buffered signal marking a backend as unreachable through its gateway
// (502/503 responses — the k8s ingress answering while no pod is ready).
// Raised by the API fetch layers on every gateway-status response, consumed
// by the UI layer that owns the outage banner and the query pause. The atom
// lives in jotai's default store — every importer in the JS realm shares
// it, and non-React emitters write through getDefaultStore().
//
// The atom holds the PENDING report set: reports raised before the consumer
// mounts (a fetch failing during app boot) collect in it and are read on
// mount. The set dedupes backends, but every report writes a FRESH Set so
// repeat reports for an already-pending backend still re-notify subscribers
// — coalescing live reports is the consumer's job, as is draining: the
// consumer clears the atom when it has processed the pending reports.
export const gatewayDownReportsAtom = atom<ReadonlySet<DownBackend>>(
  new Set<DownBackend>(),
);

// The single definition of which HTTP statuses mean "the gateway has no
// backend to serve this" (502 = pod died mid-request, 503 = no ready pods).
// 504 is deliberately excluded (verified live 2026-08-16: zero 504s in 90
// days of Sentry data): a timeout means the request may have executed — it
// must stay a visible error, not an outage signal.
export function isGatewayStatus(status: number): boolean {
  return status === 502 || status === 503;
}

// Call unconditionally on every non-ok response — the status predicate
// lives here, not at the call site.
export function reportGatewayStatus(
  backend: DownBackend,
  status: number,
): void {
  if (!isGatewayStatus(status)) return;
  const store = getDefaultStore();
  store.set(
    gatewayDownReportsAtom,
    new Set(store.get(gatewayDownReportsAtom)).add(backend),
  );
}

export function clearGatewayDownReports(): void {
  getDefaultStore().set(gatewayDownReportsAtom, new Set<DownBackend>());
}
